import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FolderSearch, Plug, Server, ShieldAlert, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import {
  SAVED_PASSWORD_MASK,
  SUPPORTED_PROTOCOLS,
  applyProtocolDefaults,
  canConnectDataSource,
  dataSourceToForm,
  formToPayload,
  parseHostInput,
  protocolDefaultPort,
} from './dataSourceHelpers';
import RemoteBrowserModal from './RemoteBrowserModal';

function SectionTitle({ children }) {
  return (
    <div className="ahri-sched__label ahri-source__section-title mb-0">
      {children}
    </div>
  );
}

function StatusLine({ result }) {
  if (!result) return null;
  if (result.ok) {
    return (
      <div className="ahri-source__status ahri-source__status--ok">
        <CheckCircle2 size={14} className="me-1" />
        {result.message || 'Connection succeeded'}
      </div>
    );
  }
  return (
    <div className="ahri-source__status ahri-source__status--err">
      <ShieldAlert size={14} className="me-1" />
      {result.message || 'Connection failed'}
    </div>
  );
}

function DataSourceSection({
  step,
  getAuthHeaders,
  onSaved,
  onDeleted,
  onError,
}) {
  const passwordRef = useRef(null);
  const [configuredSource, setConfiguredSource] = useState(step?.data_source || null);
  const [form, setForm] = useState(() => dataSourceToForm(step?.data_source));
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [passwordEditing, setPasswordEditing] = useState(false);

  // Only reset the form when opening a different pipeline step (not on every parent refresh).
  useEffect(() => {
    setConfiguredSource(step?.data_source || null);
    setForm(dataSourceToForm(step?.data_source));
    setTestResult(null);
    setPasswordEditing(false);
  }, [step?.id]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setProtocol = (value) => setForm((prev) => applyProtocolDefaults(prev, value));

  const readPassword = () => passwordRef.current?.value ?? form.password ?? '';

  const canConnect = () => canConnectDataSource(form, configuredSource, readPassword());

  const buildPayload = () => formToPayload(form, { passwordOverride: readPassword() });

  const handleTest = async () => {
    if (!canConnect()) {
      setTestResult({
        ok: false,
        message: 'Enter host, username, and password (or save credentials first).',
      });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/ahri/dashboard/data-sources/${step.id}/test`,
        { method: 'POST', headers, body: JSON.stringify(buildPayload()) },
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.detail || `Test failed (${resp.status})`);
      }
      setTestResult(payload.result || { ok: false, message: 'No response' });
    } catch (err) {
      setTestResult({ ok: false, message: err.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleBrowse = () => {
    if (!canConnect()) {
      setTestResult({
        ok: false,
        message: 'Enter host, username, and password before browsing.',
      });
      return;
    }
    setBrowserOpen(true);
  };

  const handleSave = async () => {
    if (!canConnect()) {
      onError?.('Enter host, username, and password before saving.');
      return;
    }
    setBusy(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const body = buildPayload();
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/ahri/dashboard/data-sources/${step.id}`,
        { method: 'PUT', headers, body: JSON.stringify(body) },
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.detail || `Save failed (${resp.status})`);
      }
      const saved = payload.data_source || null;
      setConfiguredSource(saved);
      setPasswordEditing(false);
      setForm((prev) => ({
        ...dataSourceToForm(saved),
        password: '',
      }));
      if (passwordRef.current) {
        passwordRef.current.value = '';
      }
      onSaved?.(step, saved);
    } catch (err) {
      onError?.(err.message || 'Unable to save data source');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!configuredSource) return;
    setRemoving(true);
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/ahri/dashboard/data-sources/${step.id}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.detail || `Delete failed (${resp.status})`);
      }
      setConfiguredSource(null);
      setForm(dataSourceToForm(null));
      if (passwordRef.current) {
        passwordRef.current.value = '';
      }
      onDeleted?.(step);
    } catch (err) {
      onError?.(err.message || 'Unable to remove data source');
    } finally {
      setRemoving(false);
    }
  };

  const hasStoredPassword = Boolean(configuredSource?.has_password);
  const typedPassword = String(readPassword()).length > 0;
  const showSavedPasswordMask = hasStoredPassword && !passwordEditing && !typedPassword;

  const handleHostBlur = () => {
    const parsed = parseHostInput(form.host, {
      username: form.username,
      remotePath: form.remote_path,
      protocol: form.protocol,
    });
    if (parsed.host !== form.host || parsed.username !== form.username
      || parsed.remote_path !== form.remote_path) {
      setForm((prev) => ({
        ...prev,
        host: parsed.host,
        username: parsed.username,
        remote_path: parsed.remote_path,
        protocol: parsed.protocol || prev.protocol,
      }));
    }
  };

  return (
    <div className="ahri-sched__section">
      <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
        <div className="ahri-source__title-row d-flex align-items-center flex-wrap">
          <SectionTitle>
            <Server size={13} className="me-1 opacity-75" aria-hidden />
            <span>Remote data source</span>
          </SectionTitle>
          {configuredSource ? (
            <span className="ahri-source__saved-badge">Saved</span>
          ) : (
            <span className="ahri-source__saved-badge ahri-source__saved-badge--muted">Not configured</span>
          )}
        </div>
        {configuredSource ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
            onClick={handleDelete}
            disabled={removing || busy}
            title="Remove stored connection"
          >
            <Trash2 size={13} />
            {removing ? '…' : 'Remove'}
          </button>
        ) : null}
      </div>

      {configuredSource?.summary ? (
        <div className="ahri-source__summary mb-3">
          <code>{configuredSource.summary}</code>
        </div>
      ) : null}

      <div className="row g-2">
        <div className="col-12 col-md-4">
          <label htmlFor={`ahri-ds-proto-${step.id}`} className="form-label small mb-1">Protocol</label>
          <select
            id={`ahri-ds-proto-${step.id}`}
            className="form-select form-select-sm"
            value={form.protocol}
            onChange={(e) => setProtocol(e.target.value)}
          >
            {SUPPORTED_PROTOCOLS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-5">
          <label htmlFor={`ahri-ds-host-${step.id}`} className="form-label small mb-1">Host</label>
          <input
            id={`ahri-ds-host-${step.id}`}
            type="text"
            className="form-control form-control-sm"
            value={form.host}
            placeholder="ftpcore.example.com"
            autoComplete="off"
            onChange={(e) => setField('host', e.target.value)}
            onBlur={handleHostBlur}
          />
          <div className="form-text">Hostname only — not a full URL (paths go in Remote folder).</div>
        </div>
        <div className="col-6 col-md-3">
          <label htmlFor={`ahri-ds-port-${step.id}`} className="form-label small mb-1">Port</label>
          <input
            id={`ahri-ds-port-${step.id}`}
            type="number"
            className="form-control form-control-sm"
            min="1"
            max="65535"
            value={form.port}
            placeholder={String(protocolDefaultPort(form.protocol))}
            onChange={(e) => setField('port', e.target.value)}
          />
        </div>
        <div className="col-12 col-md-6">
          <label htmlFor={`ahri-ds-user-${step.id}`} className="form-label small mb-1">Username</label>
          <input
            id={`ahri-ds-user-${step.id}`}
            type="text"
            className="form-control form-control-sm"
            value={form.username}
            autoComplete="off"
            onChange={(e) => setField('username', e.target.value)}
          />
        </div>
        <div className="col-12 col-md-6">
          <label htmlFor={`ahri-ds-pass-${step.id}`} className="form-label small mb-1">
            Password
            {hasStoredPassword && !passwordEditing ? (
              <span className="ahri-sched__hint ms-2">saved — click to change</span>
            ) : null}
          </label>
          <input
            key={`pwd-${step.id}-${configuredSource?.updated_at || 'new'}-${showSavedPasswordMask ? 'mask' : 'edit'}`}
            ref={passwordRef}
            id={`ahri-ds-pass-${step.id}`}
            name={`ahri-ds-password-${step.id}`}
            type={showSavedPasswordMask ? 'text' : 'password'}
            className={`form-control form-control-sm${showSavedPasswordMask ? ' ahri-source__password-mask' : ''}`}
            value={showSavedPasswordMask ? SAVED_PASSWORD_MASK : (form.password || '')}
            readOnly={showSavedPasswordMask}
            autoComplete="new-password"
            placeholder={hasStoredPassword ? 'Enter new password to replace' : 'Password'}
            onFocus={() => {
              if (hasStoredPassword && !passwordEditing) {
                setPasswordEditing(true);
                setField('password', '');
                if (passwordRef.current) {
                  passwordRef.current.value = '';
                }
              }
            }}
            onChange={(e) => setField('password', e.target.value)}
            onInput={(e) => setField('password', e.target.value)}
          />
        </div>
        <div className="col-12 col-md-8">
          <label htmlFor={`ahri-ds-path-${step.id}`} className="form-label small mb-1">Remote folder</label>
          <div className="input-group input-group-sm">
            <input
              id={`ahri-ds-path-${step.id}`}
              type="text"
              className="form-control"
              value={form.remote_path}
              placeholder="/incoming/inventory"
              onChange={(e) => setField('remote_path', e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={handleBrowse}
              disabled={busy || testing}
              title="Browse remote folder"
            >
              <FolderSearch size={14} />
              Browse
            </button>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <label htmlFor={`ahri-ds-pattern-${step.id}`} className="form-label small mb-1">File pattern</label>
          <input
            id={`ahri-ds-pattern-${step.id}`}
            type="text"
            className="form-control form-control-sm"
            value={form.file_pattern}
            placeholder="*.csv"
            onChange={(e) => setField('file_pattern', e.target.value)}
          />
        </div>
        {form.protocol !== 'sftp' ? (
          <div className="col-12 d-flex flex-wrap gap-3">
            <div className="form-check form-check-inline">
              <input
                id={`ahri-ds-passive-${step.id}`}
                type="checkbox"
                className="form-check-input"
                checked={!!form.use_passive}
                onChange={(e) => setField('use_passive', e.target.checked)}
              />
              <label className="form-check-label small" htmlFor={`ahri-ds-passive-${step.id}`}>
                Passive mode
              </label>
            </div>
            {form.protocol === 'ftp' ? (
              <div className="form-check form-check-inline">
                <input
                  id={`ahri-ds-tls-${step.id}`}
                  type="checkbox"
                  className="form-check-input"
                  checked={!!form.use_tls}
                  onChange={(e) => setField('use_tls', e.target.checked)}
                />
                <label className="form-check-label small" htmlFor={`ahri-ds-tls-${step.id}`}>
                  Upgrade to TLS
                </label>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <StatusLine result={testResult} />

      <div className="d-flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
          onClick={handleTest}
          disabled={testing || busy}
        >
          <Plug size={14} />
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
          onClick={handleSave}
          disabled={busy || testing}
        >
          {busy ? 'Saving…' : configuredSource ? 'Update data source' : 'Save data source'}
        </button>
      </div>

      <RemoteBrowserModal
        open={browserOpen}
        step={step}
        form={form}
        passwordOverride={readPassword()}
        configuredSource={configuredSource}
        initialPath={form.remote_path || '/'}
        getAuthHeaders={getAuthHeaders}
        onClose={() => setBrowserOpen(false)}
        onSelect={(path) => setField('remote_path', path)}
      />
    </div>
  );
}

export default DataSourceSection;
